<template>
  <div class="payment-invoices">
    <div class="payment-invoice-summary">
      <div class="payment-invoice-metric"><span class="payment-invoice-label">付款金额</span><strong>¥ {{ money(payableAmount) }}</strong></div>
      <div class="payment-invoice-metric"><span class="payment-invoice-label">已补发票</span><strong>¥ {{ money(value.total || 0) }}</strong></div>
      <div class="payment-invoice-metric"><span class="payment-invoice-label">待补金额</span><strong>¥ {{ money(value.remaining || 0) }}</strong></div>
      <el-button v-if="value.canCollect" type="primary" icon="el-icon-edit-outline" @click="open">补录发票</el-button>
    </div>
    <el-alert v-if="Number(application.status) === 30 && value.lastReview && value.lastReview.action === '退回补票'" :title="'总账退回：' + value.lastReview.remark" type="warning" :closable="false" show-icon />
    <el-table :data="value.invoices || []" empty-text="尚未补录发票">
      <el-table-column prop="number" label="发票号码" min-width="150" />
      <el-table-column prop="date" label="开票日期" width="115" />
      <el-table-column label="金额（元）" width="120" align="right"><template slot-scope="s">{{ money(s.row.amount) }}</template></el-table-column>
      <el-table-column label="发票附件" min-width="150"><template slot-scope="s"><file-upload :value="files(s.row.fileObjs)" read-only /></template></el-table-column>
    </el-table>
    <p v-if="application.status === 40" class="payment-invoice-note">已提交总账会计确认，确认通过后完结。</p>
    <p v-if="application.status === 50 && value.lastReview" class="payment-invoice-note">{{ value.lastReview.userName }}已确认发票，申请已完结。</p>

    <el-dialog title="补录付款发票" custom-class="payment-invoice-dialog" :visible.sync="visible" width="min(840px, 94vw)" top="6vh" append-to-body :close-on-click-modal="false" :close-on-press-escape="!busy" :show-close="!busy" :before-close="requestClose">
      <div slot="title" class="payment-invoice-dialog-heading">
        <span class="payment-invoice-dialog-icon"><i class="el-icon-tickets" aria-hidden="true" /></span>
        <div><h3>补录付款发票</h3><p>{{ application.applicationNo || '付款申请' }}</p></div>
      </div>
      <div class="payment-invoice-editor-summary" aria-live="polite">
        <div><span class="payment-invoice-label">付款金额</span><strong>¥ {{ money(payableAmount) }}</strong></div>
        <div><span class="payment-invoice-label">已录入金额</span><strong>{{ enteredCents === null ? '待完善' : '¥ ' + money(enteredTotal) }}</strong></div>
        <div class="payment-invoice-remaining"><span class="payment-invoice-label">待补金额</span><strong>{{ remainingCents === null ? '待完善' : '¥ ' + money(remainingCents / 100) }}</strong></div>
      </div>
      <el-form class="payment-invoice-editor" label-position="top" :disabled="busy" size="small" @submit.native.prevent>
        <div class="payment-invoice-editor-heading">
          <div><strong>发票明细</strong><span>{{ draft.length }} 张</span></div>
          <el-button plain icon="el-icon-plus" :disabled="busy || draft.length >= 100" @click="add">添加发票</el-button>
        </div>
        <div v-for="(row, i) in draft" :key="row.key" ref="invoiceRows" class="payment-invoice-editor-row">
          <div class="payment-invoice-row-heading"><strong>发票 {{ i + 1 }}</strong><el-button type="text" class="payment-invoice-delete" icon="el-icon-delete" :disabled="busy" @click="remove(i)">删除</el-button></div>
          <div class="payment-invoice-fields">
            <el-form-item label="发票号码" required :error="validationAttempted ? rowErrors[i].number : ''">
              <el-input v-model="row.number" placeholder="请输入发票号码" maxlength="64" :aria-label="'发票' + (i + 1) + '号码'" />
            </el-form-item>
            <el-form-item label="开票日期" required :error="validationAttempted ? rowErrors[i].date : ''">
              <el-date-picker v-model="row.date" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" :aria-label="'发票' + (i + 1) + '开票日期'" />
            </el-form-item>
            <el-form-item label="金额（元）" required :error="validationAttempted ? rowErrors[i].amount : ''">
              <el-input v-model="row.amount" placeholder="0.00" inputmode="decimal" maxlength="12" :aria-label="'发票' + (i + 1) + '金额'" />
            </el-form-item>
          </div>
          <el-form-item label="发票附件" required class="payment-invoice-attachments" :error="validationAttempted ? rowErrors[i].attachments : ''">
            <file-upload v-model="row.attachments" upload-path="/crmFinanceApplication/upload" :limit="12" :multiple="true" :read-only="busy" @uploading-change="setUploading(row.key, $event)" />
          </el-form-item>
        </div>
        <div v-if="!draft.length" class="payment-invoice-empty"><i class="el-icon-document-add" aria-hidden="true" /><p>暂无发票</p><el-button type="text" :disabled="busy" @click="add">添加第一张发票</el-button></div>
      </el-form>
      <div slot="footer" class="payment-invoice-footer">
        <div class="payment-invoice-footer-status" :class="{ 'is-ready': readyToSubmit }" aria-live="polite">
          <template v-if="uploading"><i class="el-icon-loading" /> 附件上传中</template>
          <template v-else-if="readyToSubmit"><i class="el-icon-circle-check" /> 发票已补齐，可提交总账确认</template>
          <template v-else-if="remainingCents > 0">还需补票 <strong>¥ {{ money(remainingCents / 100) }}</strong>，可先保存</template>
          <template v-else>请完善发票信息后提交</template>
        </div>
        <div class="payment-invoice-footer-actions">
          <el-button :disabled="busy" @click="requestClose()">取消</el-button>
          <el-button :disabled="busy" :loading="saving && !submitting" @click="save(false)">保存</el-button>
          <el-button type="primary" :disabled="busy || remainingCents > 0" :loading="saving && submitting" @click="save(true)">提交总账确认</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import FileUpload from '@/views/crm/components/fileUpload'
import { savePaymentInvoices } from '@/api/crm/financeApplication'
import { files, money, toCents, dateOnly } from '@/utils/expense'

export default {
  components: { FileUpload },
  props: {
    application: { type: Object, required: true },
    value: { type: Object, default: () => ({}) }
  },
  data() {
    return { visible: false, saving: false, submitting: false, draft: [], revision: null, taskId: null, validationAttempted: false, uploadingRows: {}, initialSnapshot: '' }
  },
  computed: {
    payableAmount() { return this.application.payableAmount == null ? this.application.amount : this.application.payableAmount },
    enteredCents() {
      try { return this.draft.reduce((sum, row) => sum + toCents(row.amount || '0'), 0) } catch (_) { return null }
    },
    enteredTotal() { return this.enteredCents === null ? '待完善' : (this.enteredCents / 100).toFixed(2) },
    remainingCents() { return this.enteredCents === null ? null : Math.max(0, toCents(this.payableAmount) - this.enteredCents) },
    uploading() { return Object.values(this.uploadingRows).some(Boolean) },
    busy() { return this.saving || this.uploading },
    rowErrors() {
      const numbers = this.draft.map(row => String(row.number || '').trim())
      return this.draft.map((row, index) => {
        const errors = {}
        if (!numbers[index]) errors.number = '请输入发票号码'
        else if (numbers.filter(number => number === numbers[index]).length > 1) errors.number = '发票号码不能重复'
        if (!dateOnly(row.date || '')) errors.date = '请选择有效的开票日期'
        try { if (toCents(row.amount) <= 0) errors.amount = '金额须大于 0' } catch (_) { errors.amount = String(row.amount || '').trim() ? '金额最多两位小数' : '请输入金额' }
        if (!row.attachments.length) errors.attachments = '请上传对应的发票附件'
        return errors
      })
    },
    readyToSubmit() { return this.draft.length > 0 && this.remainingCents === 0 && this.rowErrors.every(row => !Object.keys(row).length) }
  },
  methods: {
    files, money,
    open() {
      if (this.visible || this.busy) return
      this.revision = this.application.revision
      this.taskId = this.value.taskId
      this.draft = (this.value.invoices || []).map((row, index) => ({ ...row, key: 'saved-' + index, attachments: files(row.fileObjs) }))
      this.validationAttempted = false
      this.uploadingRows = {}
      if (!this.draft.length) this.add()
      this.initialSnapshot = this.snapshot()
      this.visible = true
    },
    add() {
      if (this.busy || this.draft.length >= 100) return
      this.draft.push({ key: Date.now() + '-' + Math.random(), number: '', date: '', amount: '', attachments: [] })
      if (this.visible) this.focusInvoiceRow(this.draft.length - 1)
    },
    remove(index) { if (!this.busy) this.draft.splice(index, 1) },
    setUploading(key, value) { this.$set(this.uploadingRows, key, value) },
    snapshot() { return JSON.stringify(this.draft.map(({ number, date, amount, attachments }) => ({ number, date, amount, attachments }))) },
    async requestClose(done) {
      if (this.busy) return
      if (this.snapshot() !== this.initialSnapshot) {
        try { await this.$confirm('本次修改尚未保存，确定放弃修改？', '关闭补录', { confirmButtonText: '放弃修改', cancelButtonText: '继续编辑', type: 'warning' }) } catch (_) { return }
      }
      if (typeof done === 'function') done()
      else this.visible = false
    },
    focusInvoiceRow(index, invalidOnly = false) {
      this.$nextTick(() => {
        const row = (this.$refs.invoiceRows || [])[index]
        if (!row) return
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        const input = row.querySelector(invalidOnly ? '.is-error input' : 'input')
        if (input) input.focus({ preventScroll: true })
      })
    },
    async save(submit) {
      if (this.busy) return
      this.validationAttempted = true
      const invalid = this.rowErrors.findIndex(row => Object.keys(row).length)
      if (invalid >= 0) {
        this.focusInvoiceRow(invalid, true)
        this.$message.error('第 ' + (invalid + 1) + ' 张发票：' + Object.values(this.rowErrors[invalid])[0])
        return
      }
      if (submit && !this.readyToSubmit) {
        this.$message.error('请补齐本次付款的发票后再提交总账确认')
        return
      }
      this.saving = true
      this.submitting = submit
      try {
        const invoices = this.draft.map(row => ({ number: String(row.number).trim(), date: row.date, amount: row.amount, fileObjs: JSON.stringify(row.attachments) }))
        await savePaymentInvoices({ id: this.application.id, revision: this.revision, taskId: this.taskId, invoices, submit })
        this.visible = false
        this.$message.success(submit ? '已提交总账确认' : '发票已保存，可稍后继续补录')
        this.$emit('saved')
      } catch (e) {
        this.$message.error(e.message || '保存失败，请重试')
      } finally {
        this.saving = false
        this.submitting = false
      }
    }
  }
}
</script>
<style lang="scss">
.payment-invoices {
  .payment-invoice-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 24px; margin-bottom: 24px; }
  .payment-invoice-metric { display: grid; gap: 8px; }
  .payment-invoice-label { font-size: 13px; color: #8491a5; }
  .payment-invoice-metric strong { font-size: 18px; color: #354c70; font-variant-numeric: tabular-nums; }
  .payment-invoice-summary > .el-button { margin-left: auto; }
  > .el-alert { margin-bottom: 16px; }
  .payment-invoice-note { font-size: 13px; color: #8491a5; line-height: 1.7; margin-top: 18px; }
}
.payment-invoice-dialog {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  margin-bottom: 6vh;
  border-radius: 12px;
  overflow: hidden;
  color: #35465e;
  .el-dialog__header { flex-shrink: 0; padding: 22px 26px; border-bottom: 1px solid #edf0f5; }
  .el-dialog__headerbtn { top: 25px; right: 24px; font-size: 20px; }
  .el-dialog__body { min-height: 0; overflow-y: auto; padding: 22px 26px 6px; }
  .el-dialog__footer { flex-shrink: 0; padding: 18px 26px; border-top: 1px solid #edf0f5; background: #f8fafc; }
  .payment-invoice-dialog-heading { display: flex; align-items: center; gap: 12px; padding-right: 26px; }
  .payment-invoice-dialog-icon { display: flex; align-items: center; justify-content: center; width: 42px; height: 42px; flex-shrink: 0; border-radius: 10px; background: #edf3ff; color: #4480f3; font-size: 23px; }
  .payment-invoice-dialog-heading h3 { margin: 0 0 4px; color: #2d3e55; font-size: 19px; line-height: 1.4; font-weight: 600; }
  .payment-invoice-dialog-heading p { margin: 0; font-size: 12px; line-height: 1.5; color: #8491a5; overflow-wrap: anywhere; }
  .payment-invoice-editor-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; padding: 16px 20px; margin-bottom: 20px; border: 1px solid #e6ebf2; border-radius: 8px; background: #f7f9fd; }
  .payment-invoice-editor-summary > div { display: grid; gap: 8px; min-width: 0; }
  .payment-invoice-label { font-size: 12px; color: #7d8ba0; }
  .payment-invoice-editor-summary strong { font-size: 19px; color: #354c70; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .payment-invoice-remaining strong { color: #4480f3; }
  .payment-invoice-editor-heading, .payment-invoice-row-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .payment-invoice-editor-heading { margin-bottom: 8px; }
  .payment-invoice-editor-heading strong { font-size: 15px; color: #2d3e55; }
  .payment-invoice-editor-heading > div > span { margin-left: 10px; font-size: 12px; color: #8491a5; }
  .payment-invoice-editor-heading .el-button { border-color: #d9e3f4; color: #4480f3; }
  .payment-invoice-editor-row { padding: 12px 0 4px; border-top: 1px solid #edf0f5; }
  .payment-invoice-editor-row:first-of-type { border-top: 0; }
  .payment-invoice-row-heading { margin-bottom: 10px; }
  .payment-invoice-row-heading strong { font-size: 13px; font-weight: 500; color: #6f7f94; }
  .payment-invoice-delete { color: #8592a5; padding: 4px 0; }
  .payment-invoice-delete:hover { color: #f56c6c; }
  .payment-invoice-fields { display: grid; grid-template-columns: minmax(180px, 1fr) 170px 140px; gap: 16px; }
  .el-form-item { margin-bottom: 18px; }
  .el-form-item__label { font-size: 13px; line-height: 20px; padding: 0 0 7px; color: #60738f; }
  .el-date-editor { width: 100%; }
  .el-input__inner { height: 36px; line-height: 36px; border-radius: 5px; }
  .el-input__icon { line-height: 36px; }
  .payment-invoice-attachments { margin-bottom: 16px; }
  .payment-invoice-attachments .el-form-item__label { float: left; width: 84px; padding: 0 12px 0 0; line-height: 32px; }
  .payment-invoice-attachments .el-form-item__content { margin-left: 84px; line-height: 1; }
  .upload-files .el-upload-list__item, .upload-files .el-upload--picture-card { width: 64px; height: 64px; margin: 0 8px 6px 0; border-radius: 5px; }
  .upload-files .el-upload--picture-card { line-height: 64px; background: #fafbfd; }
  .upload-files .el-upload--picture-card i { font-size: 20px; }
  .upload-files .image { width: 64px; height: 64px; }
  .upload-files .doc .doc-icon { font-size: 24px; padding-top: 7px; }
  .upload-files .doc .doc-name { padding: 2px 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 20px; }
  .upload-files .el-upload__tip { margin: 0; font-size: 12px; color: #99a4b5; }
  .upload-files .el-upload-list__item-actions { font-size: 16px; }
  .upload-files .el-upload-list__item-actions span + span { margin-left: 10px; }
  .payment-invoice-empty { padding: 20px 0 30px; text-align: center; color: #8491a5; }
  .payment-invoice-empty > i { font-size: 30px; color: #bcc7d8; }
  .payment-invoice-empty p { margin: 10px 0 0; }
  .payment-invoice-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px; }
  .payment-invoice-footer-status { font-size: 12px; color: #7d8ba0; text-align: left; line-height: 1.6; }
  .payment-invoice-footer-status strong { color: #354c70; font-weight: 500; }
  .payment-invoice-footer-status.is-ready { color: #288568; }
  .payment-invoice-footer-actions { display: flex; justify-content: flex-end; gap: 10px; margin-left: auto; }
  .payment-invoice-footer-actions .el-button { min-width: 72px; height: 38px; margin: 0; border-radius: 6px; padding: 10px 16px; }
}
@media (max-width: 650px) {
  .payment-invoice-dialog {
    .el-dialog__header, .el-dialog__footer { padding: 16px; }
    .el-dialog__body { padding: 16px 16px 4px; }
    .el-dialog__headerbtn { top: 20px; right: 16px; }
    .payment-invoice-editor-summary { gap: 10px; padding: 12px; }
    .payment-invoice-editor-summary strong { font-size: 15px; }
    .payment-invoice-fields { grid-template-columns: minmax(0, 1fr) 130px; gap: 0 12px; }
    .payment-invoice-fields .el-form-item:first-child { grid-column: 1 / -1; }
    .payment-invoice-footer { gap: 8px; }
  }
  .payment-invoices .payment-invoice-summary { gap: 14px; }
}
</style>
